/**
 * Benachrichtigungs-Glocke (Topbar) + Drawer — Merge 2026-07-18 aus zwei Strängen:
 *  - main (#18): Aktivitäts-Drawer über GET /api/activity + datengetriebener Unseen-Punkt
 *    (localStorage merkt den letzten Besuch).
 *  - CX-Audit 2026-07-17: Sektion „Braucht Aufmerksamkeit" aus Live-Daten (überfällige/
 *    fehlgeschlagene Zahlungen, liegengebliebene Anfragen, ablaufende/angenommene Angebote) —
 *    JEDE Meldung navigiert zum Vorgang; dazu Event-Label im Feed (API-Join) und Klickziele
 *    auch für Einträge ohne eventId (inquiry.created -> /anfragen). Footer verlinkt die
 *    Benachrichtigungs-Einstellungen.
 *
 * Umbau P4 (Antoine, 07.09.2026): "neue Anfragen fehlen in der Glocke / sind irgendwo unten
 * nicht auffindbar". Befund: eine einzige flache, nach Dringlichkeit sortierte Liste mit hartem
 * Cap(8) NACH der Sortierung — bei vielen ablaufenden Angeboten (immer urgent:true) fielen frische
 * Anfragen (nur nach 48h urgent) komplett aus der Liste, nicht nur "weiter unten". Seither DREI
 * Ebenen statt einer:
 *  1. „Wartet auf dich" (`baueWartetAufDich`) — echte Kundenaktionen, die eine menschliche
 *     Reaktion brauchen: neue Anfrage (ab sofort IMMER dringend, nicht erst nach 48h),
 *     Änderungswunsch von der öffentlichen Angebotsseite (K5.2, `offer.aenderungswunsch` — fehlte
 *     bislang als Notice komplett), Zahlung fehlgeschlagen/überfällig, Angebot angenommen.
 *     Bewusst OHNE Cap — das war genau der Fehler.
 *  2. „Bald fällig" (`baueBaldFaellig`) — ablaufende Angebote, GEBÜNDELT zu einer Zeile ab dem
 *     zweiten Treffer, damit sie Ebene 1 nie wieder verdrängen können.
 *  3. „Zuletzt passiert" — das bestehende `ACTION_META`-Aktivitätsprotokoll, unverändert.
 * Alle drei Builder sind reine, exportierte Funktionen (wie `zaehleWartetAufDich` in
 * AnfragenGruppen.tsx) — Regel-Fehler damit in Sekunden ohne DOM/Netz prüfbar.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useMitHerkunft, useSeitenKontext } from "../lib/rueckweg";
import { aktiverTitel } from "./navigation";
import { useApi } from "../hooks/useApi";
import type { EventRow, Inquiry, Payment } from "./types";
import { rechnungGrundText } from "../lib/activity-labels";
import { anlassAnzeige, formatDate, money } from "./format";
import { ANFRAGEN_PFAD, vorgangPfad, zahlungenPfad } from "../lib/pfade";
import { Button, Drawer, Icon } from "./components";
import { useCopilot } from "./CopilotProvider";
import { anlassName, type SaisonKampagne } from "./SaisonKampagneDrawer";

export interface ActivityEntry {
  id: string;
  eventId: string | null;
  actorType: "user" | "customer" | "system";
  actorLabel: string | null;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
  /** Merge: Anzeige-Label aus dem API-Join (leftJoin events) — null bei tenant-weiten Einträgen. */
  eventNumber?: string | null;
  eventOccasion?: string | null;
}

/** Zahlung inkl. Event-Kontext, wie sie GET /api/payments (aggregiert) liefert. */
export interface PaymentWithEvent extends Payment {
  eventLabel: string;
  eventNumber: string | null;
  customerId: string;
}

/** Eine Meldung in „Wartet auf dich" oder „Bald fällig": immer mit Klickziel — keine tote Anzeige. */
export interface Notice {
  key: string;
  icon: string;
  title: string;
  sub: string;
  to: string;
  urgent: boolean;
  at: number;
}

const ACTION_META: Record<string, { label: string; icon: string }> = {
  "inquiry.created": { label: "Anfrage eingegangen", icon: "inbox" },
  // MAESTRO 2.0 Phase C v1: autonomer Angebots-Entwurf (shadow-first). Traegt event_id des
  // Entwurfs -> feedTarget verlinkt automatisch auf /veranstaltungen/:id (nichts wurde gesendet).
  "offer.draft_prepared": { label: "MAESTRO hat einen Entwurf vorbereitet", icon: "smart_toy" },
  // K5.2 v1 (E-K11): Betreiber-Karte nach einer Gast-Mail auf ein versendetes Angebot -
  // die Variante selbst liegt im Builder, hier nur der Hinweis.
  "offer.v2_vorgeschlagen": { label: "MAESTRO hat nach einer Gast-Antwort eine angepasste Variante entworfen", icon: "smart_toy" },
  // S3.2 (KONZEPT-GEDAECHTNIS.md § 2b, Entstehungsweg (c) "Agent-Entwurf aus Nutzung"): der
  // periodische Konsolidierungslauf (S2.2-Sweep) erkennt ein wiederkehrendes Muster (viele
  // Werkzeugschritte oder wiederholte Ablehnung) und schlaegt EINEN Playbook-Entwurf vor -
  // niemals selbst aktiviert (konsolidiere(), 170_gedaechtnis_agent_playbook_vorschlag.sql).
  "playbook.draft_prepared": { label: "MAESTRO hat einen Skill-Entwurf vorgeschlagen", icon: "smart_toy" },
  // Referenz-Automatik P4.5: EINE Meldung je Vorgang (Upload-Stapel bzw. Sweep-Lauf), nicht
  // eine je Datei - siehe meldeVorgang() in apps/api/src/lib/reference-pipeline.ts.
  "reference.analysis_finished": { label: "Referenz-Angebote analysiert", icon: "fact_check" },
  "reference.analysis_failed": { label: "Referenz-Analyse fehlgeschlagen", icon: "error" },
  "style_profile.draft_ready": { label: "Stilprofil-Entwurf liegt zur Durchsicht bereit", icon: "auto_fix_high" },
  // Seit 30.07.2026 der Normalfall: Das Profil ist SCHARF und formt ab sofort neue Angebote.
  // Bewusst anders benannt als der Entwurf - "bereit" und "wirkt" sind nicht dasselbe, und der
  // Betreiber muss auf einen Blick sehen, welches von beidem gilt.
  "style_profile.activated": { label: "Stilprofil ist aktiv — es prägt ab jetzt neue Angebote", icon: "check_circle" },
  "event.status_changed": { label: "Status geändert", icon: "sync_alt" },
  "event.cancelled": { label: "Vorgang storniert", icon: "cancel" },
  "offer.sent": { label: "Angebot gesendet", icon: "send" },
  "offer.resent": { label: "Angebot erneut gesendet", icon: "send" },
  "offer.chosen": { label: "Angebot angenommen", icon: "celebration" },
  "offer.restored": { label: "Version wiederhergestellt", icon: "history" },
  // Punkt 61 (19.09.2026): der Gast hat bei einem Wahlmenü entschieden, welches Gericht gilt.
  "menu.wahl_getroffen": { label: "Gast hat sein Gericht gewählt", icon: "restaurant_menu" },
  "cost_acceptance.requested": { label: "Kostenübernahme angefordert", icon: "draw" },
  // Punkt 57 Etappe 3 (19.09.2026): der GAST hat selbst ausgelöst — deshalb eine eigene
  // Beschriftung. „Kostenübernahme angefordert" allein ließe offen, wer es war, und genau das
  // ist bei einem Vertrag die Frage, die später zählt.
  "cost_acceptance.gast_angefordert": { label: "Gast hat die Kostenübernahme angefordert", icon: "draw" },
  "cost_acceptance.gast_zugestellt": { label: "Kostenübernahme für den Gast bereitgestellt", icon: "assignment_turned_in" },
  "cost_acceptance.signed": { label: "Kostenübernahme signiert", icon: "verified" },
  "cost_acceptance.withdrawn": { label: "Kostenübernahme zurückgezogen", icon: "undo" },
  // Punkt 57 (19.09.2026): der Betreiber erlaubt dem GAST, die Kostenübernahme selbst anzufordern —
  // einmalig, bis er sie einlöst. Zwei Codes, weil Erteilen und Entziehen zwei Entscheidungen sind.
  "cost_acceptance.gast_freigabe_erteilt": { label: "Gast darf die Kostenübernahme selbst anfordern", icon: "lock_open" },
  "cost_acceptance.gast_freigabe_entzogen": { label: "Freigabe für den Gast zurückgenommen", icon: "lock" },
  "invoice.created": { label: "Rechnung erstellt", icon: "receipt_long" },
  "rechnung.nicht_erstellt": { label: "Rechnung nicht erstellt", icon: "report" },
  "rechnung.erstellt": { label: "Rechnung erstellt", icon: "receipt_long" },
  "rechnung.uebersprungen": { label: "Keine automatische Rechnung nötig", icon: "info" },
  "rechnung.versand_fehlgeschlagen": { label: "Rechnung nicht gesendet", icon: "report" },
  "invoice.sent": { label: "Rechnung gesendet", icon: "mail" },
  "invoice.cancelled": { label: "Rechnung storniert", icon: "undo" },
  "payment.recorded": { label: "Zahlung erfasst", icon: "payments" },
  "payment.refunded": { label: "Zahlung erstattet", icon: "payments" },
  "customer.anonymized": { label: "Kunde anonymisiert", icon: "person_off" },
};
const ACTOR_LABEL: Record<ActivityEntry["actorType"], string> = { user: "Team", customer: "Kunde", system: "System" };
const LS_KEY = "maestro.activity.lastSeen";

function meta(action: string): { label: string; icon: string } {
  return ACTION_META[action] ?? { label: action, icon: "bolt" };
}
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tg.`;
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** CX-Audit: Klickziel je Feed-Eintrag — Event-Detail, sonst der passende Bereich (nie tot,
 *  außer es gibt schlicht kein sinnvolles Ziel, z. B. „Kunde anonymisiert").
 *  CX-Fix 2026-07-18: inquiry.created trägt details.inquiry_id -> Deep-Link führt auf die
 *  Detailseite der KONKRETEN Anfrage statt nur die Liste. */
/** Saeulen-Namen wortgleich zur Katalog-Seite (PILLAR_META in pages/catalog-references.tsx). */
const SAEULE_LABEL: Record<string, string> = {
  catering: "Catering (außer Haus)",
  event: "Event (im Haus)",
};

/**
 * Zusatz hinter dem Titel, damit gleichlautende Meldungen unterscheidbar sind.
 *
 * Antoine am 30.07.2026 an einem Bildschirmfoto: zwei identische Zeilen "Stilprofil ist aktiv"
 * ohne jeden Hinweis, WELCHES Profil gemeint war. Die Saeule stand in `details`, nur nicht in der
 * Anzeige. Fuer Referenz-Analysen gilt dasselbe mit der Anzahl der Dokumente.
 */
function detailLabel(e: ActivityEntry): string | null {
  const d = e.details;
  if (!d) return null;
  if (e.action.startsWith("style_profile.")) {
    const saeule = typeof d["pillar"] === "string" ? (d["pillar"] as string) : null;
    return saeule ? (SAEULE_LABEL[saeule] ?? saeule) : null;
  }
  if (e.action === "reference.analysis_finished") {
    const n = typeof d["analysed"] === "number" ? (d["analysed"] as number) : null;
    return n == null ? null : n === 1 ? "1 Dokument" : `${n} Dokumente`;
  }
  if (e.action === "rechnung.nicht_erstellt" || e.action === "rechnung.uebersprungen") return rechnungGrundText(d["grund"]);
  if (e.action === "rechnung.versand_fehlgeschlagen") return typeof d["grund"] === "string" ? (d["grund"] as string) : null;
  if (e.action === "reference.analysis_failed") {
    const f = typeof d["failed"] === "number" ? (d["failed"] as number) : null;
    const t = typeof d["total"] === "number" ? (d["total"] as number) : null;
    if (f == null) return null;
    return t == null ? `${f} gescheitert` : `${f} von ${t} gescheitert`;
  }
  return null;
}

/** Klartextgrund eines Eintrags, sofern die Meldung einen mitliefert (details.reason). */
function reasonOf(e: ActivityEntry): string | null {
  const r = e.details && typeof e.details["reason"] === "string" ? (e.details["reason"] as string).trim() : "";
  return r.length > 0 ? r : null;
}

function feedTarget(e: ActivityEntry): string | null {
  if (e.eventId) return vorgangPfad(e.eventId);
  if (e.action === "inquiry.created") {
    const iid = e.details && typeof e.details["inquiry_id"] === "string" ? (e.details["inquiry_id"] as string) : null;
    return iid ? `/anfragen/${iid}` : "/anfragen";
  }
  // Punkt 115 (Paket 1): ohne Vorgang wenigstens auf den passenden Reiter.
  if (e.action === "payment.succeeded") return zahlungenPfad({ filter: "paid" });
  if (e.action === "payment.refund_initiated" || e.action === "payment.refunded_external") return zahlungenPfad({ filter: "refunded" });
  if (e.action.startsWith("payment.")) return zahlungenPfad();
  // Referenz-Automatik (P4.5): dort stehen die Dokumente und der Stilprofil-Entwurf.
  // Wissen-Umbau 05.08.2026: die Referenzen-Flaeche ist von /katalog nach /wissen umgezogen.
  if (e.action.startsWith("reference.") || e.action.startsWith("style_profile.")) return "/gedaechtnis?tab=lernt";
  // S3.2: Playbook-Entwuerfe (auch die vom Agenten) liegen im Reiter „Wie MAESTRO handelt" auf
  // /wissen (Alt-Key ?tab=playbooks wird dort weiterhin abgebildet).
  if (e.action === "playbook.draft_prepared") return "/gedaechtnis?tab=handelt";
  return null;
}

/**
 * Anzeige-Label eines Vorgangs OHNE den Fallback-Fehler von vorher (Antoine an einem
 * Bildschirmfoto: mehrere Zeilen zeigten wortgleich "Angebot läuft ab — Veranstaltung", weil
 * `occasion` fehlte und direkt auf das Wort "Veranstaltung" zurückgefallen wurde — nicht
 * unterscheidbar). Fehlt `occasion`, wird aus dem kombiniert, was tatsächlich vorliegt: Kunde
 * (nur bekannt bei GET /api/events/:id — die hier verwendete Liste GET /api/events liefert ihn
 * NICHT, siehe Kommentar an `EventRow.customerName`), Datum, Gästezahl. Erst wenn wirklich gar
 * nichts davon da ist, bleibt die Vorgangsnummer bzw. zuletzt das alte Wort übrig.
 */
export function eventAnzeigeLabel(e: EventRow): string {
  if (e.occasion?.trim()) return anlassAnzeige(e.occasion)!;
  const teile = [e.customerName ?? null, e.eventDate ? formatDate(e.eventDate) : null, e.guestCount ? `${e.guestCount} Gäste` : null].filter(
    (t): t is string => Boolean(t),
  );
  if (teile.length > 0) return teile.join(" · ");
  // Posteingang S2: die Vorgangsnummer ist kein Titel (seit Migration 541 hat sie jeder Vorgang).
  return "Vorgang";
}

/**
 * Kundenname je Vorgang. F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): `i.id` ist seit AP5
 * bereits die EVENT-ID (kein `inquiries.generatedEventId` -> `events.id`-Join mehr nötig,
 * `generatedEventId` ist deshalb aus dem `Inquiry`-Typ entfallen). Gebraucht an zwei Stellen: für
 * den „Änderungswunsch"-Punkt in `baueWartetAufDich` UND als Ersatz für die IP-Adresse im
 * Aktivitäts-Feed (`actorAnzeige`). Vorgänge ohne erzeugende Anfrage (manuell angelegt) liefern
 * keinen Treffer — dann bleibt es bei der Vorgangs-Zusammenfassung (`eventAnzeigeLabel`) statt
 * geraten zu werden.
 */
export function kundennameNachEventId(inquiries: Inquiry[] | null | undefined): Map<string, string> {
  const m = new Map<string, string>();
  for (const i of inquiries ?? []) {
    m.set(i.id, i.customerName);
  }
  return m;
}

/**
 * Anzeige-Text für den Aktivitäts-Feed (Ebene 3, „Zuletzt passiert").
 *
 * BEFUND (Screenshot, Antoine): Gast-Aktionen (Änderungswunsch, Frage, Annahme, Absage,
 * Nachfass-Antwort/-Abmeldung) schrieben die IP des Gasts als `activity_log.actor_label`. Seit
 * sql/663 steht dort der Kundenname (Rückfall „Gast“) — alte Zeilen behalten ihre IP (kein
 * Backfill). Darum: ein Label, das keine IP ist, wird angezeigt; eine IP nie — statt ihrer der
 * Kundenname aus dem Anfrage-Join (`kundennameNachEventId`), sonst „Kunde“.
 */
export function actorAnzeige(e: ActivityEntry, kundenname: Map<string, string>): string | null {
  const label = e.actorLabel?.trim();
  if (label && !istIpAdresse(label)) return label;
  if (!label) return null;
  return (e.eventId && kundenname.get(e.eventId)) || "Kunde";
}

/** Grobe Erkennung alter IP-Labels (IPv4 oder IPv6) — reicht, um sie nie anzuzeigen. */
export function istIpAdresse(s: string): boolean {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(s) || (s.includes(":") && /^[0-9a-f:.]+$/i.test(s));
}

/**
 * Ebene 1 — „Wartet auf dich": echte Kundenaktionen, die eine menschliche Reaktion brauchen.
 * ABSICHTLICH OHNE Cap (siehe Dateikopf) — das Kappen einer bereits nach Dringlichkeit
 * sortierten Liste war genau der Fehler, den Antoine gemeldet hat.
 */
export function baueWartetAufDich(args: {
  inquiries: Inquiry[] | null | undefined;
  events: EventRow[] | null | undefined;
  payments: PaymentWithEvent[] | null | undefined;
  activity: ActivityEntry[] | null | undefined;
  /**
   * Phase C.2 WP-E (Postfach-Zuordnung, main): eigene, guenstige Zaehl-Route statt der teuren
   * `/api/inbox` (Kandidaten-Vorschlaege je Gruppe) - siehe routes/inbox.ts. `ohneVorgang` zaehlt
   * GRUPPEN (channel+from_addr_norm), dieselbe Zahl wie der Tab „Ohne Vorgang n" in
   * posteingang.tsx und das Nav-Badge (AppShell.tsx) - EINE Zaehlwahrheit (P10.6-Muster). Optional,
   * weil nicht jeder Aufrufer (z. B. Tests der anderen Meldungen) die Route mitgeben muss.
   */
  inboxZaehler?: { ohneVorgang: number; ungelesen: number } | null;
  /** Punkt 101 Stufe 2: Saison-Kampagnen mit offenen (vorbereiteten, nicht freigegebenen) Vorschlägen. */
  saisonKampagnen?: { anlass: string; jahr: number; offen: number; stichtag: string }[] | null;
  /** Saison-Mails (Entscheidung 5, 26.09.2026): neues Saisonmenü der Website — nur ein Hinweis. */
  menueHinweise?: { id: string; anlass: string; jahr: number; titel: string; erkanntAt: string }[] | null;
  now: number;
}): Notice[] {
  const { inquiries, events, payments, activity, inboxZaehler, saisonKampagnen, menueHinweise, now } = args;
  const list: Notice[] = [];

  for (const h of menueHinweise ?? []) {
    list.push({
      key: `menue-hinweis-${h.id}`,
      icon: "restaurant_menu",
      title: "Neues Menü erkannt – Kampagne anlegen?",
      sub: `„${h.titel}“ · ${anlassName(h.anlass)} ${h.jahr} · es wird nichts verschickt`,
      to: "/kunden/vormerkungen",
      urgent: false,
      at: new Date(h.erkanntAt).getTime(),
    });
  }

  for (const k of saisonKampagnen ?? []) {
    if (k.offen <= 0) continue;
    const anlass = anlassName(k.anlass);
    list.push({
      key: `saison-${k.anlass}-${k.jahr}`,
      icon: "celebration",
      title: `${k.offen} ${k.offen === 1 ? "Menüvorschlag" : "Menüvorschläge"} ${anlass} bereit`,
      sub: `Saison-Kampagne ${anlass} ${k.jahr} · noch nichts verschickt — geht erst nach Ihrer Freigabe hinaus`,
      to: "/freigaben",
      urgent: true,
      at: new Date(`${k.stichtag}T06:00:00Z`).getTime(),
    });
  }
  const eventById = new Map((events ?? []).map((e) => [e.id, e]));
  const kundenname = kundennameNachEventId(inquiries);

  // Post ohne Vorgang (Phase C.2 WP-E): braucht eine Zuordnungs-Entscheidung vom Team, gehoert
  // darum in dieselbe uncapped Ebene wie neue Anfragen/Aenderungswuensche, nicht in eine Zeit-
  // gebundene "Bald faellig"-Ebene.
  const ohneVorgang = inboxZaehler?.ohneVorgang ?? 0;
  if (ohneVorgang > 0) {
    const ungelesen = inboxZaehler?.ungelesen ?? 0;
    list.push({
      key: "posteingang-ohne-vorgang",
      icon: "forum",
      title: `Post ohne Vorgang wartet — ${ohneVorgang} Absender`,
      sub: ungelesen > 0 ? `${ungelesen} davon ungelesen` : "Zuordnen oder ablegen",
      to: "/posteingang?tab=unassigned",
      urgent: ungelesen > 0,
      at: now,
    });
  }

  for (const p of payments ?? []) {
    if (!["pending", "processing"].includes(p.status) || !p.dueDate) continue;
    const due = new Date(p.dueDate).getTime();
    if (due >= now) continue;
    const days = Math.max(1, Math.round((now - due) / 86_400_000));
    list.push({
      key: `pay-${p.id}`,
      icon: "account_balance_wallet",
      title: `Zahlung überfällig — ${p.eventLabel}`,
      sub: `${money(p.amountCents)} · seit ${days} Tag${days === 1 ? "" : "en"} fällig`,
      to: vorgangPfad(p.eventId),
      urgent: true,
      at: due,
    });
  }

  for (const e of events ?? []) {
    if (e.status === "payment_failed") {
      list.push({
        key: `fail-${e.id}`,
        icon: "error",
        title: `Zahlung fehlgeschlagen — ${eventAnzeigeLabel(e)}`,
        sub: "Zahlungsweg im Vorgang klären",
        to: vorgangPfad(e.id),
        urgent: true,
        at: new Date(e.createdAt).getTime(),
      });
    }
    // Migration 726/727 (Antoine 16.10.2026 „Ja, Meldung“): nach Teilerstattung steht ein Rest offen.
    // Kein Fehler -> nicht urgent (rot nur für Fehler).
    if (e.status === "balance_due") {
      list.push({
        key: `rest-${e.id}`,
        icon: "hourglass_empty",
        title: `Restforderung offen — ${eventAnzeigeLabel(e)}`,
        sub: "Restbetrag beim Kunden anfordern",
        to: vorgangPfad(e.id),
        urgent: false,
        at: new Date(e.createdAt).getTime(),
      });
    }
    if (e.status === "offer_chosen") {
      list.push({
        key: `won-${e.id}`,
        icon: "celebration",
        title: `Angebot angenommen — ${eventAnzeigeLabel(e)}`,
        sub: "Rechnung/Zahlung einrichten",
        to: vorgangPfad(e.id),
        urgent: false,
        at: new Date(e.createdAt).getTime(),
      });
    }
  }

  for (const i of inquiries ?? []) {
    if (i.status !== "new") continue;
    const age = now - new Date(i.createdAt).getTime();
    const stale = age > 48 * 3_600_000;
    list.push({
      key: `inq-${i.id}`,
      icon: "inbox",
      title: `Neue Anfrage — ${i.customerName}`,
      sub: [i.guests ? `${i.guests} Gäste` : null, stale ? `wartet seit ${Math.round(age / 3_600_000)} h` : "frisch eingegangen — Angebot erstellen"]
        .filter(Boolean)
        .join(" · "),
      // CX-Fix 2026-07-18: direkt zur einen Vorgangsseite DIESER Anfrage, nicht nur zur Liste.
      // F2: `i.id` ist bereits die Event-ID (AP5) — kein Umweg über den `/anfragen/:id`-Resolver
      // nötig, wenn das Ziel schon bekannt ist (dieselbe Ueberlegung wie `uebersetzeAltesZiel`).
      to: vorgangPfad(i.id),
      // Fix 07.09.2026 (Antoine: "fehlen ... obwohl sie eigentlich wichtig sind"): vorher erst
      // nach 48h dringend — eine frische Anfrage braucht JETZT eine Reaktion, nicht erst wenn sie
      // liegen geblieben ist. `stale` steuert nur noch den Untertext ("frisch" vs. "wartet
      // schon lange"), beide erscheinen und beide sind dringend.
      urgent: true,
      at: new Date(i.createdAt).getTime(),
    });
  }

  // NEU: Änderungswunsch von der öffentlichen Angebotsseite (K5.2, `offer.aenderungswunsch`) —
  // fehlte bislang komplett als Notice, tauchte nur roh im Aktivitäts-Feed auf (Dateikopf). Nur
  // EINER je Vorgang (`activity` kommt absteigend sortiert von GET /api/activity, also der
  // jüngste zuerst) und nur solange der Vorgang noch bei "Angebot gesendet" steht — danach ist
  // der Wunsch beantwortet (neues Angebot, Annahme, Absage, Stornierung) und keine offene
  // Handlung mehr.
  const gesehen = new Set<string>();
  for (const a of activity ?? []) {
    // B2: auch die Frage von der Angebotsseite (`offer.gast_frage`) wartet auf eine Antwort.
    const frage = a.action === "offer.gast_frage";
    // Gastseite zur Anfrage (sql/674): Nachricht VOR dem Angebot — offen, bis das Angebot rausgeht.
    const gastseite = a.action === "anfrage.gast_nachricht";
    if ((a.action !== "offer.aenderungswunsch" && !frage && !gastseite) || !a.eventId || gesehen.has(a.eventId)) continue;
    gesehen.add(a.eventId);
    const ev = eventById.get(a.eventId);
    if (!ev || (gastseite ? ev.status !== "inquiry" && ev.status !== "offer_draft" : ev.status !== "offer_sent")) continue;
    list.push({
      key: `wunsch-${a.eventId}`,
      icon: frage || gastseite ? "help" : "edit_note",
      title: `${gastseite ? "Nachricht des Gastes" : frage ? "Frage des Gastes" : "Änderungswunsch"} — ${kundenname.get(a.eventId) ?? eventAnzeigeLabel(ev)}`,
      sub: gastseite ? "Kunde hat über die Gastseite etwas zur Anfrage geschickt" : frage ? "Kunde hat eine Frage zum Angebot" : "Kunde möchte das Angebot angepasst haben",
      to: vorgangPfad(a.eventId),
      urgent: true,
      at: new Date(a.createdAt).getTime(),
    });
  }

  // Rechnungs-Automatik (sql/742, Antoine 26.10.2026): zu einer Zahlung entstand KEINE Rechnung,
  // und der Betrieb muss etwas tun (Adresse nachtragen, Rechnungsprogramm einrichten, Betrag
  // pruefen). Eine Meldung je ZAHLUNG (nicht je Vorgang: eine zweite Zahlung am selben Vorgang hat
  // ihre eigene Rechnung). Sie verschwindet, sobald zu DERSELBEN Zahlung ein juengerer Marker
  // `rechnung.erstellt` (oder ein juengeres invoice.created mit derselben payment_id) steht;
  // `activity` kommt absteigend, das Juengere also zuerst. Nach 30 Tagen faellt sie aus der Glocke
  // (der Verlauf des Vorgangs behaelt sie). Nur der Stripe-Webhook schreibt den Marker.
  const zahlungGemeldet = new Set<string>();
  // Sofortversand (sql/744): die Rechnung ist da, ging aber nicht hinaus (oder die Bitte um die
  // Adresse ging nicht hinaus). ZWEI DURCHGAENGE, unabhaengig von der Sortierung der Eintraege:
  // erst alles sammeln, was eine Meldung erledigt, dann melden.
  //  - Rechnung nicht gesendet: erledigt, sobald zu DIESER Rechnung ein `invoice.sent` steht.
  //  - Adress-Bitte nicht gesendet (Eintrag ohne invoice_id): erledigt, sobald zur Zahlung eine
  //    Rechnung entstand (`rechnung.erstellt` / `invoice.created` mit payment_id) - die Bitte ist
  //    dann hinfaellig. Der spaetere Versand des Ausgangs selbst hinterlaesst keinen Verlaufseintrag.
  //    Erledigt heisst: der Erledigt-Eintrag ist NICHT aelter als die Meldung (ein altes
  //    invoice.sent / rechnung.erstellt loest einen spaeteren Fehler nicht auf).
  const rechnungVersendet = new Map<string, number>(); // invoice_id -> juengstes invoice.sent
  const zahlungErledigt = new Map<string, number>(); // payment_id -> juengstes rechnung.erstellt / invoice.created
  const juengstes = (m: Map<string, number>, k: string, t: number) => m.set(k, Math.max(m.get(k) ?? 0, t));
  const erledigtNach = (m: Map<string, number>, k: string, t: number) => (m.get(k) ?? -Infinity) >= t;
  const versandGemeldet = new Set<string>();
  for (const a of activity ?? []) {
    const iid = typeof a.details?.["invoice_id"] === "string" ? (a.details["invoice_id"] as string) : null;
    const pid = typeof a.details?.["payment_id"] === "string" ? (a.details["payment_id"] as string) : null;
    const t = new Date(a.createdAt).getTime();
    if (a.action === "invoice.sent" && iid) juengstes(rechnungVersendet, iid, t);
    if ((a.action === "rechnung.erstellt" || a.action === "invoice.created") && pid) juengstes(zahlungErledigt, pid, t);
  }
  for (const a of activity ?? []) {
    const iid = typeof a.details?.["invoice_id"] === "string" ? (a.details["invoice_id"] as string) : null;
    const pid = typeof a.details?.["payment_id"] === "string" ? (a.details["payment_id"] as string) : null;
    if (!pid) continue;
    if (a.action === "rechnung.versand_fehlgeschlagen") {
      if (!a.eventId || versandGemeldet.has(pid)) continue;
      const tFehler = new Date(a.createdAt).getTime();
      if (iid ? erledigtNach(rechnungVersendet, iid, tFehler) : erledigtNach(zahlungErledigt, pid, tFehler)) continue;
      if (now - new Date(a.createdAt).getTime() > 30 * 86_400_000) continue;
      versandGemeldet.add(pid);
      const ev = eventById.get(a.eventId);
      list.push({
        key: `rechnung-versand-${pid}`,
        icon: "report",
        title: `Rechnung nicht gesendet — ${kundenname.get(a.eventId) ?? (ev ? eventAnzeigeLabel(ev) : "Vorgang")}`,
        sub: "Der Versand an den Gast ist gescheitert; MAESTRO versucht es stündlich erneut. Die Mail liegt in der Freigabe-Liste.",
        to: vorgangPfad(a.eventId),
        urgent: true,
        at: new Date(a.createdAt).getTime(),
      });
      continue;
    }
    if (a.action !== "rechnung.nicht_erstellt" || !a.eventId || erledigtNach(zahlungErledigt, pid, new Date(a.createdAt).getTime()) || zahlungGemeldet.has(pid)) continue;
    const alter = now - new Date(a.createdAt).getTime();
    if (alter > 30 * 86_400_000) continue;
    zahlungGemeldet.add(pid);
    const ev = eventById.get(a.eventId);
    list.push({
      key: `rechnung-fehlt-${pid}`,
      icon: "report",
      title: `Rechnung nicht erstellt — ${kundenname.get(a.eventId) ?? (ev ? eventAnzeigeLabel(ev) : "Vorgang")}`,
      sub: rechnungGrundText(a.details?.["grund"]) ?? "Bitte den Vorgang prüfen",
      to: vorgangPfad(a.eventId),
      urgent: true,
      at: new Date(a.createdAt).getTime(),
    });
  }

  return list.sort((a, b) => (a.urgent === b.urgent ? b.at - a.at : a.urgent ? -1 : 1));
}

/**
 * Ebene 2 — „Bald fällig": ablaufende Angebote. Ab dem zweiten Treffer GEBÜNDELT zu einer
 * Sammelzeile (Fund: einzeln waren das N wortgleiche "Angebot läuft ab — …"-Zeilen, und weil sie
 * alle urgent:true waren, konnten sie unter dem alten Cap(8) frische, aber urgent:false Anfragen
 * komplett verdrängen). Ziel der Sammelzeile: die Anfragen-Liste — `offer_sent` ist seit F2
 * (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md, Antoines Entscheidung D2) ein vor-Zusage-Status
 * und erscheint in der Buchungen-Liste (ab Zusage) nicht mehr; der frühere Status-Filter
 * `?filter=offer_sent` auf `/buchungen` existiert dort deshalb nicht mehr.
 */
export function baueBaldFaellig(events: EventRow[] | null | undefined, now: number): Notice[] {
  const in3days = now + 3 * 86_400_000;
  const ablaufend = (events ?? []).filter(
    (e): e is EventRow & { offerValidUntil: string } =>
      e.status === "offer_sent" && Boolean(e.offerValidUntil) && new Date(e.offerValidUntil!).getTime() <= in3days,
  );
  if (ablaufend.length === 0) return [];

  if (ablaufend.length === 1) {
    const e = ablaufend[0]!;
    const until = new Date(e.offerValidUntil).getTime();
    return [
      {
        key: `exp-${e.id}`,
        icon: "hourglass_bottom",
        title: `Angebot läuft ab — ${eventAnzeigeLabel(e)}`,
        sub: until < now ? "Gültigkeit bereits verstrichen" : "Gültig bis in Kürze — nachfassen",
        to: vorgangPfad(e.id),
        urgent: true,
        at: until,
      },
    ];
  }

  const fristen = ablaufend.map((e) => new Date(e.offerValidUntil).getTime());
  return [
    {
      key: "exp-bundle",
      icon: "hourglass_bottom",
      title: `${ablaufend.length} Angebote laufen in den nächsten 3 Tagen ab`,
      sub: "Zur Anfragen-Liste — nachfassen",
      to: ANFRAGEN_PFAD,
      urgent: true,
      at: Math.min(...fristen),
    },
  ];
}

/** Eine Zeile in „Wartet auf dich" oder „Bald fällig" — dieselbe Optik für beide Ebenen. */
function NoticeRow({ n, onGo }: { n: Notice; onGo: (to: string) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onGo(n.to)}
        className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-surface-container-high dark:hover:bg-white/5"
      >
        <span
          className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${
            n.urgent ? "bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/22 dark:text-[#e39b92]" : "bg-primary/15 text-primary"
          }`}
        >
          <Icon name={n.icon} className="text-[17px]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-medium text-on-surface dark:text-inverse-on-surface">{n.title}</span>
          <span className="block truncate font-body-sm text-[12px] text-on-surface-variant/80">{n.sub}</span>
        </span>
        <Icon name="chevron_right" className="mt-1 shrink-0 text-[16px] text-on-surface-variant/50" />
      </button>
    </li>
  );
}

export function NotificationsBell() {
  const navigate = useNavigate();
  // Das Ausweichen vor der Copilot-Spalte war frueher hier von Hand verdrahtet (offsetForCopilot).
  // Von 31.07.2026 bis 01.09.2026 machte das jeder Drawer selbst automatisch (siehe Kommentar an
  // `Drawer` in ui/components.tsx) — diese Stelle war zuvor die einzige von 22, die daran gedacht
  // hatte. Der Mechanismus ist inzwischen komplett entfernt (Drawer ist jetzt immer rechts
  // buendig), dieser Kommentar bleibt nur als Erklaerung, warum hier nichts Besonderes mehr steht.
  const [open, setOpen] = useState(false);
  // Feed laden: beim Mount (fuer den Punkt) und immer wenn der Drawer offen ist (reload).
  const { data, reload } = useApi<ActivityEntry[]>("/api/activity?limit=40");
  const entries = useMemo(() => data ?? [], [data]);

  // Ebenen 1+2 — aus denselben tenant-lokalen Listen wie das Dashboard (+ dem bereits geladenen
  // Aktivitäts-Feed für den neuen Änderungswunsch-Punkt, siehe `baueWartetAufDich`).
  const inquiriesQ = useApi<Inquiry[]>("/api/inquiries");
  const eventsQ = useApi<EventRow[]>("/api/events");
  const paymentsQ = useApi<PaymentWithEvent[]>("/api/payments");
  // Phase C.2 WP-E (Postfach-Zuordnung): eigene, guenstige Zaehl-Route statt der teuren
  // `/api/inbox` (Kandidaten-Vorschlaege je Gruppe) - siehe routes/inbox.ts. `ohneVorgang` zaehlt
  // GRUPPEN (channel+from_addr_norm), dieselbe Zahl wie der Tab „Ohne Vorgang n" in
  // posteingang.tsx und das Nav-Badge (AppShell.tsx) - EINE Zaehlwahrheit (P10.6-Muster).
  const inboxZaehlerQ = useApi<{ ohneVorgang: number; ungelesen: number }>("/api/inbox/zaehler");
  const saisonQ = useApi<SaisonKampagne[]>("/api/saison-kampagnen");
  const menueHinweiseQ = useApi<{ id: string; anlass: string; jahr: number; titel: string; erkanntAt: string }[]>("/api/saison-kampagnen/hinweise");

  const kundenname = useMemo(() => kundennameNachEventId(inquiriesQ.data), [inquiriesQ.data]);

  const wartetAufDich = useMemo<Notice[]>(
    () =>
      baueWartetAufDich({
        inquiries: inquiriesQ.data,
        events: eventsQ.data,
        payments: paymentsQ.data,
        activity: entries,
        inboxZaehler: inboxZaehlerQ.data,
        saisonKampagnen: saisonQ.data,
        menueHinweise: menueHinweiseQ.data,
        now: Date.now(),
      }),
    [inquiriesQ.data, eventsQ.data, paymentsQ.data, entries, inboxZaehlerQ.data, saisonQ.data, menueHinweiseQ.data],
  );

  const baldFaellig = useMemo<Notice[]>(() => baueBaldFaellig(eventsQ.data, Date.now()), [eventsQ.data]);

  const urgentCount = wartetAufDich.filter((n) => n.urgent).length + baldFaellig.filter((n) => n.urgent).length;

  const [lastSeen, setLastSeen] = useState<string>(() => {
    try {
      return localStorage.getItem(LS_KEY) ?? "";
    } catch {
      return "";
    }
  });

  const newest = entries[0]?.createdAt ?? "";
  const hasUnseen = newest !== "" && newest > lastSeen;
  const unseenCount = lastSeen === "" ? entries.length : entries.filter((e) => e.createdAt > lastSeen).length;

  useEffect(() => {
    if (open) {
      reload();
      inquiriesQ.reload();
      eventsQ.reload();
      paymentsQ.reload();
      inboxZaehlerQ.reload();
      saisonQ.reload();
      menueHinweiseQ.reload();
      if (newest) {
        try {
          localStorage.setItem(LS_KEY, newest);
        } catch {
          /* localStorage evtl. blockiert - Punkt bleibt, kein harter Fehler */
        }
        setLastSeen(newest);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Punkt 115 (G2): ein Sprung aus der Glocke auf einen Vorgang merkt sich die Seite, auf der man war.
  const mitHier = useMitHerkunft();
  const { pathname } = useLocation();
  const hierTitel = useSeitenKontext()?.titel ?? aktiverTitel(pathname);
  function go(to: string) {
    setOpen(false);
    navigate(to.startsWith(vorgangPfad("")) ? mitHier(to, hierTitel) : to);
  }

  return (
    <>
      <button
        type="button"
        className="relative rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10"
        title="Benachrichtigungen"
        aria-label={
          urgentCount > 0
            ? `Benachrichtigungen (${urgentCount} dringend)`
            : hasUnseen
              ? `Benachrichtigungen (${unseenCount} neu)`
              : "Benachrichtigungen"
        }
        onClick={() => setOpen(true)}
      >
        <Icon name="notifications" />
        {urgentCount > 0 ? (
          <span className="absolute right-0.5 top-0.5 grid h-[16px] min-w-[16px] place-items-center rounded-full bg-[#B23B32] px-1 text-[9.5px] font-bold text-white">
            {urgentCount > 9 ? "9+" : urgentCount}
          </span>
        ) : hasUnseen ? (
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#C93E32] ring-2 ring-[#FFFDF8] dark:ring-night-bar" />
        ) : null}
      </button>

      {/* Portal an body: die Kopfzeile (z-30) bildet einen eigenen Stapel — darin lag der Drawer
          mobil UNTER der unteren Navigationsleiste (z-40), die seine Fußleiste verdeckte. */}
      {createPortal(
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Benachrichtigungen"
        subtitle={
          wartetAufDich.length > 0 || baldFaellig.length > 0 || entries.length > 0
            ? [
                wartetAufDich.length > 0
                  ? `${wartetAufDich.length} offene${wartetAufDich.length === 1 ? "r" : ""} Punkt${wartetAufDich.length === 1 ? "" : "e"}`
                  : null,
                baldFaellig.length > 0 ? `${baldFaellig.length} bald fällig` : null,
                entries.length > 0 ? `letzte ${entries.length} Aktivitäten` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined
        }
        footer={
          <Button variant="secondary" onClick={() => go("/mein-konto?tab=benachrichtigungen")}>
            <Icon name="settings" className="text-[16px]" /> Einstellungen für Benachrichtigungen
          </Button>
        }
      >
        {/* Ebene 1: Wartet auf dich — echte Kundenaktionen, KEIN Cap (siehe Dateikopf). */}
        {wartetAufDich.length > 0 && (
          <div className="mb-5">
            <p className="mb-2 font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-on-surface-variant/70">
              Wartet auf dich
            </p>
            <ul className="space-y-1">
              {wartetAufDich.map((n) => (
                <NoticeRow key={n.key} n={n} onGo={go} />
              ))}
            </ul>
          </div>
        )}

        {/* Ebene 2: Bald fällig — ablaufende Angebote, ab dem zweiten Treffer gebündelt. */}
        {baldFaellig.length > 0 && (
          <div className="mb-5">
            <p className="mb-2 font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-on-surface-variant/70">
              Bald fällig
            </p>
            <ul className="space-y-1">
              {baldFaellig.map((n) => (
                <NoticeRow key={n.key} n={n} onGo={go} />
              ))}
            </ul>
          </div>
        )}

        {/* Ebene 3: Aktivitäts-Feed (main #18), mit Event-Label + Klickzielen — unverändert. */}
        {(wartetAufDich.length > 0 || baldFaellig.length > 0) && entries.length > 0 && (
          <p className="mb-2 font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-on-surface-variant/70">
            Zuletzt passiert
          </p>
        )}
        {entries.length === 0 && wartetAufDich.length === 0 && baldFaellig.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <Icon name="notifications_off" className="text-3xl text-on-surface-variant/60" />
            <p className="font-body-sm text-body-sm text-on-surface-variant">Noch keine Aktivitäten.</p>
          </div>
        ) : (
          <ul className="space-y-1">
            {entries.map((e) => {
              const m = meta(e.action);
              const unseen = lastSeen === "" || e.createdAt > lastSeen;
              const target = feedTarget(e);
              // Event-Label wo es eines gibt, sonst der Zusatz aus den Details (Saeule/Anzahl).
              const eventLabel = anlassAnzeige(e.eventOccasion) ?? detailLabel(e);
              return (
                <li key={e.id}>
                  <button
                    type="button"
                    disabled={!target}
                    onClick={() => {
                      if (target) go(target);
                    }}
                    className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                      target ? "hover:bg-surface-container-high dark:hover:bg-white/5" : "cursor-default"
                    }`}
                  >
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${unseen ? "bg-primary/15 text-primary" : "bg-surface-container-high text-on-surface-variant dark:bg-white/5"}`}>
                      <Icon name={m.icon} className="text-[17px]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-[13.5px] font-medium text-on-surface dark:text-inverse-on-surface">
                          {m.label}
                          {eventLabel ? ` — ${eventLabel}` : ""}
                        </span>
                        {unseen && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                      </span>
                      <span className="block font-body-sm text-[12px] text-on-surface-variant/80">
                        {ACTOR_LABEL[e.actorType]}
                        {actorAnzeige(e, kundenname) ? ` · ${actorAnzeige(e, kundenname)}` : ""} · {timeAgo(e.createdAt)}
                      </span>
                      {/* Klartextgrund, wo einer mitkommt (z. B. eine fehlgeschlagene
                          Referenz-Analyse, P4.5). Eine Fehlermeldung ohne Grund zwingt den
                          Betreiber nur zum Nachsehen. */}
                      {reasonOf(e) && (
                        <span className="mt-0.5 block font-body-sm text-[12px] text-on-surface-variant">{reasonOf(e)}</span>
                      )}
                    </span>
                    {target && <Icon name="chevron_right" className="mt-1 shrink-0 text-[16px] text-on-surface-variant/50" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Drawer>,
        document.body,
      )}
    </>
  );
}
